import { Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { SentToDOTDto } from 'dtos/conformity'
import { ConformityListContext } from 'enums/conformity/ConformityListContext'
import { useEffect, useState } from 'react'
import { clientSidePagination, formatDateMoment } from 'utils'
import ConformityZipDownloadLink from './ConformityZipDownloadLink'
import ConformityZipPreviewLink from './ConformityZipPreviewLink'
import DownloadSummarySheetLink from './DownloadSummarySheetLink'

interface SentToDOTCardProps {
  sentToDOT: SentToDOTDto[]
}

export default function SentToDOTCard({ sentToDOT }: SentToDOTCardProps) {
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  useEffect(() => {
    setPage(0)
  }, [pageSize])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Sent To DOT
            </Typography>
          </Grid>

          {sentToDOT.slice(...clientSidePagination(page, pageSize)).map(inboundTrip => (
            <Grid item xs={12}>
              <Card variant='outlined'>
                <CardContent>
                  <Grid container spacing={1} alignItems='flex-start'>
                    <Grid item xs={12} sm={3} container spacing={1} alignItems='flex-start'>
                      <Grid item xs={12}>
                        <LabelField label='Entry Date' value={inboundTrip.entryDate ? formatDateMoment(inboundTrip.entryDate) : undefined} />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Entry #' value={inboundTrip.entryNumber} />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Port of Entry' value={inboundTrip.portOfEntry} />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Port Code' value={inboundTrip.portCode} />
                      </Grid>

                      <Grid item xs={12}>
                        <DownloadSummarySheetLink
                          context={ConformityListContext.SentToDOT}
                          entryNumber={inboundTrip.entryNumber}
                          inboundTripGuid={inboundTrip.inboundTripGuid}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={9}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Importer Stock #</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Exporter</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>PAPS #</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>VIN</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Year</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Make</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Model</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Conformity Zip Preview</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Conformity Zip Download</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Conformity Package Tracking #</TableCell>

                              <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Sent To DOT</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {inboundTrip.vehicles.map((vehicle, index) => (
                              <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                                <TableCell>{vehicle.importerStockNumber}</TableCell>

                                <TableCell>{vehicle.exporterCompany}</TableCell>

                                <TableCell>{vehicle.papsNumber}</TableCell>

                                <TableCell>{vehicle.vin}</TableCell>

                                <TableCell>{vehicle.year}</TableCell>

                                <TableCell>{vehicle.make}</TableCell>

                                <TableCell>{vehicle.model}</TableCell>

                                <TableCell>
                                  <ConformityZipPreviewLink vehicleGuid={vehicle.vehicleGuid} />
                                </TableCell>

                                <TableCell>
                                  <ConformityZipDownloadLink vehicleGuid={vehicle.vehicleGuid} vin={vehicle.vin} />
                                </TableCell>

                                <TableCell>{vehicle.conformityPackageTrackingNumber}</TableCell>

                                <TableCell>{vehicle.sentToDOTDateTime ? formatDateMoment(vehicle.sentToDOTDateTime) : undefined}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12} alignItems='center' justifyItems='flex-end'>
            <TablePagination
              count={sentToDOT.length ?? 0}
              labelRowsPerPage='Cards per page:'
              onPageChange={(_, page) => setPage(page)}
              onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              SelectProps={{ fullWidth: false }}
              sx={{ borderBottom: 'none' }}
            />
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Sent To DOT Table:**

Vehicles appear in the “Sent To DOT” table when the vehicle has a “Conformity Package Tracking #” and “Sent To DOT” date under Vehicle Details – Conformity.

Vehicles are grouped by their Entry #, which is retrieved from the Inbound Trip the vehicle is associated with. The following details are also pulled from the Inbound Trip:
   - Entry Date
   - Entry #
   - Port of Entry
   - Port Code

**Download Summary Sheet Button:**

Clicking Download Summary Sheet within a Conformity Vehicle Group downloads an Excel file named:

   - Conformity Summary Sheet {Entry #}.xlsx

This file contains a row for each vehicle within the group.

**Conformity Zip Preview Column:**

Clicking “Preview” in a vehicle's row opens a browser tab or pop-up displaying each document and photo included in that vehicle’s Conformity Zip.

**Conformity Zip Download Column:**

Clicking “Download” in a vehicle’s row downloads that vehicle’s Conformity Zip to the user’s machine.

**Conformity Package Tracking # Column:**

Displays the Conformity Package Tracking # that was entered from the Ready To Send Table.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
